import { ASSET_TYPE_LABELS, PLATFORM_LABELS } from "./constants";
import { getAssetReviewWorkflow } from "./reviewWorkflow";

const SURFACE_BY_TYPE = {
  thread: "thread",
  carousel_outline: "carousel",
  video_script: "vertical video",
  image_prompt: "image prompt",
  blog: "blog draft",
  manual_post: "manual post",
  imported_draft: "imported draft",
};

const REVIEW_STATE = {
  draft: {
    tone: "review",
    primaryActionLabel: "Approve",
    actionHint: "Finish edits before this can be scheduled.",
  },
  needs_review: {
    tone: "review",
    primaryActionLabel: "Approve",
    actionHint: "Approve before this can be scheduled.",
  },
  approved: {
    tone: "approved",
    primaryActionLabel: "Schedule",
    actionHint: "Ready for calendar scheduling.",
  },
  scheduled: {
    tone: "scheduled",
    primaryActionLabel: "Reschedule",
    actionHint: "Queued on the calendar.",
  },
  publishing: {
    tone: "scheduled",
    primaryActionLabel: "Publishing",
    actionHint: "Publishing workflow is in progress.",
  },
  published: {
    tone: "published",
    primaryActionLabel: "Published",
    actionHint: "Recorded as published.",
  },
  archived: {
    tone: "archived",
    primaryActionLabel: "Archived",
    actionHint: "Archived from the active queue.",
  },
};

function labelFromId(id = "") {
  return String(id)
    .split("_")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function hasText(value) {
  return typeof value === "string" && value.trim() && value.trim() !== "\u2014";
}

export function getPostText(asset = {}) {
  const hashtags = Array.isArray(asset.hashtags) ? asset.hashtags.join(" ") : asset.hashtags;
  return [asset.hook, asset.body, asset.cta, hashtags]
    .filter(hasText)
    .join("\n\n");
}

function getThreadSegments(asset = {}) {
  const bodySegments = String(asset.body || "")
    .split(/\n\n+/)
    .map((segment) => segment.trim())
    .filter(hasText);
  return [asset.hook, ...bodySegments, asset.cta]
    .map((segment) => String(segment || "").trim())
    .filter(hasText);
}

export function getPlatformPreviewMeta(asset = {}) {
  const platform = asset.platform || "linkedin";
  const assetType = asset.asset_type || "social_post";
  const platformLabel = PLATFORM_LABELS[platform] || labelFromId(platform) || "Platform";
  const assetTypeLabel = ASSET_TYPE_LABELS[assetType] || labelFromId(assetType) || "Social Post";
  const surfaceLabel = platform === "youtube_shorts" || assetType === "video_script"
    ? "vertical video"
    : SURFACE_BY_TYPE[assetType] || (platform === "instagram" ? "feed post" : "post");
  const threadSegments = assetType === "thread" ? getThreadSegments(asset) : [];
  const calculatedTextLength = threadSegments.length > 0
    ? Math.max(...threadSegments.map((segment) => segment.length))
    : getPostText(asset).length;
  const textLength = calculatedTextLength || (Number.isFinite(asset.char_count) ? asset.char_count : 0);
  const characterLabel = asset.char_limit
    ? `${textLength}/${asset.char_limit} chars${assetType === "thread" ? " max post" : ""}`
    : `${textLength} chars`;

  return {
    platform,
    assetType,
    platformLabel,
    assetTypeLabel,
    surfaceLabel,
    previewTitle: `${platformLabel} ${surfaceLabel} preview`,
    previewNote: `Rendered as a ${surfaceLabel} so review happens against the final channel format.`,
    characterLabel,
    textLength,
    charLimit: asset.char_limit || null,
    overLimit: Boolean(asset.char_limit && (
      threadSegments.length > 0
        ? threadSegments.some((segment) => segment.length > asset.char_limit)
        : textLength > asset.char_limit
    )),
  };
}

export function getPostReviewWorkspace(asset = {}) {
  const workflow = getAssetReviewWorkflow(asset);
  const state = REVIEW_STATE[workflow.status] || REVIEW_STATE.draft;

  return {
    ...workflow,
    ...state,
    status: workflow.status,
    decisionTitle: workflow.handoffTitle,
    decisionCopy: workflow.handoffCopy,
    showApprovalActions: workflow.canApprove || workflow.status === "approved" || workflow.status === "scheduled",
  };
}

export function getMobilePreviewPolicy(asset = {}, context = "review") {
  if (asset.asset_type === "image_prompt") {
    return {
      collapsible: false,
      collapsedHeight: null,
      defaultExpanded: true,
      expandLabel: "Show preview",
      collapseLabel: "Collapse preview",
    };
  }

  const editor = context === "editor";

  return {
    collapsible: true,
    collapsedHeight: editor ? 360 : 420,
    defaultExpanded: false,
    expandLabel: editor ? "Show full live preview" : "Show full platform preview",
    collapseLabel: "Collapse preview",
  };
}
